import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { CoursePageLayout } from "@/components/site/CoursePageLayout";
import { getCourse, type Course } from "@/content/courses";
import { SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/course-catalog/$slug")({
  loader: ({ params }): { course: Course } => {
    const course = getCourse(params.slug);
    if (!course) throw notFound();
    return { course };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Course unavailable | Leadership Edge Live" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { course } = loaderData;
    const description =
      course.tagline ??
      `${course.title}: a live, facilitator-led leadership course from Leadership Edge Live.`;
    return {
      links: [{ rel: "canonical", href: `${SITE_URL}/course-catalog/${params.slug}/` }],
      meta: [
        { title: `${course.title} | Leadership Edge Live` },
        { name: "description", content: description },
        { property: "og:title", content: `${course.title} | Leadership Edge Live` },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(course.image?.startsWith("http")
          ? [
              { property: "og:image", content: course.image },
              { name: "twitter:image", content: course.image },
            ]
          : []),
      ],
    };
  },
  errorComponent: () => <CourseMissing message="Something went wrong loading this course." />,
  notFoundComponent: () => <CourseMissing message="We couldn't find that course." />,
  component: CourseDetail,
});

function CourseMissing({ message }: { message: string }) {
  return (
    <Section>
      <h1 className="font-display text-3xl text-ink">{message}</h1>
      <Link
        to="/course-catalog/"
        className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary"
      >
        Back to the catalog
        <ArrowRight className="h-4 w-4" />
      </Link>
    </Section>
  );
}

function CourseDetail() {
  const { course } = Route.useLoaderData() as { course: Course };
  return <CoursePageLayout course={course} />;
}
